import { WebGLContext } from './WebGLContext';
import { WebGLUtils } from './WebGLUtils';
import { SimulationConfig } from '@/types';

const UPDATE_VERTEX_SHADER = `#version 300 es
in vec2 a_pos;
in vec2 a_vel;
in vec3 a_color;
in float a_life;
in float a_maxLife;

out vec2 v_pos;
out vec2 v_vel;
out vec3 v_color;
out float v_life;
out float v_maxLife;

uniform float u_deltaTime;
uniform float u_gravity;
uniform float u_damping;

// Simple block pseudo-random for GPU particle noise
float hash(float n) { return fract(sin(n) * 753.5453123); }

void main() {
    if (a_life <= 0.0) {
        v_pos = a_pos;
        v_vel = vec2(0.0);
        v_color = a_color;
        v_life = 0.0;
        v_maxLife = a_maxLife;
    } else {
        // Decay lifetime
        v_life = a_life - (u_deltaTime / a_maxLife);
        
        // Gravity integration
        vec2 gravityAccel = vec2(0.0, u_gravity);
        v_vel = a_vel + gravityAccel * u_deltaTime;
        v_vel *= u_damping;
        
        // Integrate position
        v_pos = a_pos + v_vel * u_deltaTime;
        v_color = a_color;
        v_maxLife = a_maxLife;
    }
}
`;

const UPDATE_FRAGMENT_SHADER = `#version 300 es
precision highp float;
// Trivial empty fragment shader for Transform Feedback pipeline discarded rasterizer
out vec4 outColor;
void main() {
    outColor = vec4(0.0);
}
`;

const RENDER_VERTEX_SHADER = `#version 300 es
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec3 a_color;
layout(location = 2) in float a_life;

uniform vec2 u_resolution;
uniform vec2 u_translate;
uniform float u_scale;
uniform float u_baseSize;

out vec3 v_color;
out float v_life;

void main() {
    v_color = a_color;
    v_life = a_life;
    
    if (a_life <= 0.0) {
        gl_Position = vec4(-2.0, -2.0, -2.0, 1.0);
        gl_PointSize = 0.0;
    } else {
        // Viewport camera transform
        vec2 camPos = (a_pos * u_scale) + u_translate;
        vec2 clipPos = (camPos / u_resolution) * 2.0 - 1.0;
        clipPos.y *= -1.0;
        
        gl_Position = vec4(clipPos, 0.0, 1.0);
        gl_PointSize = u_baseSize * u_scale * a_life;
    }
}
`;

const RENDER_FRAGMENT_SHADER = `#version 300 es
precision highp float;
in vec3 v_color;
in float v_life;
out vec4 fragColor;

void main() {
    float dist = length(gl_PointCoord - vec2(0.5));
    if (dist > 0.5) discard;
    
    // Aesthetic neon/soft bloom decay
    float glow = exp(-3.8 * dist);
    float alpha = smoothstep(0.5, 0.1, dist);
    
    fragColor = vec4(v_color, glow * v_life * alpha);
}
`;

export class GPUParticleSystem {
  private updateProgram: WebGLProgram;
  private renderProgram: WebGLProgram;

  // Double Buffering structures for WebGL2 Feedback Ping-Pong
  private buffers: WebGLBuffer[][] = []; // [BufferSetA, BufferSetB]
  private transformFeedbacks: WebGLTransformFeedback[] = [];
  private updateVaos: WebGLVertexArrayObject[] = [];
  private renderVaos: WebGLVertexArrayObject[] = [];

  private activeIndex = 0; // Toggle ping-pong active source buffer index
  private maxParticles = 20000;
  private writeHead = 0; // Direct CPU emission circular ring buffer pointer

  // Temporary arrays for particle uploading
  private tempPos: Float32Array;
  private tempVel: Float32Array;
  private tempColor: Float32Array;
  private tempLife: Float32Array;
  private tempMaxLife: Float32Array;

  // Pre-allocated buffers for zero-allocation particle emitting & resetting
  private emitPos = new Float32Array(1000 * 2);
  private emitVel = new Float32Array(1000 * 2);
  private emitColor = new Float32Array(1000 * 3);
  private emitLife = new Float32Array(1000);
  private emitMaxLife = new Float32Array(1000);
  private glZeros: Float32Array;

  constructor(private context: WebGLContext) {
    const gl = context.gl;

    // --- 1. COMPILE UPDATE PROGRAM WITH VARYINGS SPECIFICATION ---
    const uvShader = WebGLUtils.createShader(gl, gl.VERTEX_SHADER, UPDATE_VERTEX_SHADER);
    const ufShader = WebGLUtils.createShader(gl, gl.FRAGMENT_SHADER, UPDATE_FRAGMENT_SHADER);
    
    this.updateProgram = gl.createProgram()!;
    gl.attachShader(this.updateProgram, uvShader);
    gl.attachShader(this.updateProgram, ufShader);

    // Tell WebGL which outputs our feedback writes should capture
    const varyings = ['v_pos', 'v_vel', 'v_color', 'v_life', 'v_maxLife'];
    gl.transformFeedbackVaryings(this.updateProgram, varyings, gl.SEPARATE_ATTRIBS);
    
    gl.linkProgram(this.updateProgram);
    if (!gl.getProgramParameter(this.updateProgram, gl.LINK_STATUS)) {
      console.error('Transform Feedback Update linking error:', gl.getProgramInfoLog(this.updateProgram));
    }

    // --- 2. COMPILE RENDER PROGRAM ---
    this.renderProgram = WebGLUtils.createProgram(gl, RENDER_VERTEX_SHADER, RENDER_FRAGMENT_SHADER);

    // --- 3. ALLOCATE GPU DOUBLE BUFFERS ---
    // Buffer index layouts match standard input parameters:
    // 0: pos (vec2), 1: vel (vec2), 2: color (vec3), 3: life (float), 4: maxLife (float)
    const sizes = [
      this.maxParticles * 2 * 4, // vec2 pos (float32)
      this.maxParticles * 2 * 4, // vec2 vel (float32)
      this.maxParticles * 3 * 4, // vec3 color (float32)
      this.maxParticles * 1 * 4, // float life
      this.maxParticles * 1 * 4  // float maxLife
    ];

    this.tempPos = new Float32Array(this.maxParticles * 2);
    this.tempVel = new Float32Array(this.maxParticles * 2);
    this.tempColor = new Float32Array(this.maxParticles * 3);
    this.tempLife = new Float32Array(this.maxParticles);
    this.tempMaxLife = new Float32Array(this.maxParticles);
    this.glZeros = new Float32Array(this.maxParticles * 3);

    // Generate arrays for Ping-Pong sources
    for (let setIdx = 0; setIdx < 2; setIdx++) {
      const vbos: WebGLBuffer[] = [];
      for (let attribIdx = 0; attribIdx < sizes.length; attribIdx++) {
        const buf = gl.createBuffer()!;
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        // Allocate space and fill with unactive values
        gl.bufferData(gl.ARRAY_BUFFER, sizes[attribIdx], gl.DYNAMIC_COPY);
        vbos.push(buf);
      }
      this.buffers.push(vbos);

      // Create Transform Feedback target descriptors
      const tf = gl.createTransformFeedback()!;
      gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, tf);
      for (let attribIdx = 0; attribIdx < vbos.length; attribIdx++) {
        gl.bindBufferBase(gl.TRANSFORM_FEEDBACK_BUFFER, attribIdx, vbos[attribIdx]);
      }
      this.transformFeedbacks.push(tf);
    }

    // --- 4. CONSTRUCT RETRIEVAL VAOS FOR BOTH UPDATE AND RENDER ---
    for (let setIdx = 0; setIdx < 2; setIdx++) {
      const activeBufs = this.buffers[setIdx];

      // Update VAO layout (5 attributes to match vertex shaders inputs)
      const uVao = gl.createVertexArray()!;
      gl.bindVertexArray(uVao);
      
      // Position
      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[0]);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      // Velocity
      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[1]);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 0, 0);

      // Color
      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[2]);
      gl.enableVertexAttribArray(2);
      gl.vertexAttribPointer(2, 3, gl.FLOAT, false, 0, 0);

      // Life
      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[3]);
      gl.enableVertexAttribArray(3);
      gl.vertexAttribPointer(3, 1, gl.FLOAT, false, 0, 0);

      // MaxLife
      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[4]);
      gl.enableVertexAttribArray(4);
      gl.vertexAttribPointer(4, 1, gl.FLOAT, false, 0, 0);

      this.updateVaos.push(uVao);

      // Render VAO layout (Needs 3 fields: position, color, life)
      const rVao = gl.createVertexArray()!;
      gl.bindVertexArray(rVao);

      // pos at location=0, color at location=1, life at location=2
      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[0]);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[2]);
      gl.enableVertexAttribArray(1);
      gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);

      gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[3]);
      gl.enableVertexAttribArray(2);
      gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 0, 0);

      this.renderVaos.push(rVao);
    }

    gl.bindVertexArray(null);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);
  }

  emit(posX: number, posY: number, colorHex: string, count = 20, speedMult = 1.0, duration = 800) {
    const gl = this.context.gl;

    // Get RGB color
    const c = WebGLUtils.hexToRgb(colorHex);
    const r = c[0] / 255;
    const g = c[1] / 255;
    const b = c[2] / 255;

    const actualCount = Math.min(count, this.maxParticles - this.writeHead);
    if (actualCount <= 0) return;

    // Expand persistent local buffers dynamically if we ever emit a giant burst
    if (this.emitPos.length < actualCount * 2) {
      const newCap = actualCount * 2;
      this.emitPos = new Float32Array(newCap * 2);
      this.emitVel = new Float32Array(newCap * 2);
      this.emitColor = new Float32Array(newCap * 3);
      this.emitLife = new Float32Array(newCap);
      this.emitMaxLife = new Float32Array(newCap);
    }

    for (let i = 0; i < actualCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = (2.0 + Math.random() * 8.0) * speedMult * 0.12;

      this.emitPos[i * 2] = posX;
      this.emitPos[i * 2 + 1] = posY;

      this.emitVel[i * 2] = Math.cos(angle) * speed;
      this.emitVel[i * 2 + 1] = Math.sin(angle) * speed;

      this.emitColor[i * 3] = r;
      this.emitColor[i * 3 + 1] = g;
      this.emitColor[i * 3 + 2] = b;

      this.emitLife[i] = 1.0; // Initial normalized life
      this.emitMaxLife[i] = duration; // Milliseconds duration
    }

    // Determine current writing destination
    // Write directly into the currently active Ping-Pong source set
    const activeSet = this.buffers[this.activeIndex];

    gl.bindBuffer(gl.ARRAY_BUFFER, activeSet[0]);
    gl.bufferSubData(gl.ARRAY_BUFFER, this.writeHead * 2 * 4, this.emitPos.subarray(0, actualCount * 2));

    gl.bindBuffer(gl.ARRAY_BUFFER, activeSet[1]);
    gl.bufferSubData(gl.ARRAY_BUFFER, this.writeHead * 2 * 4, this.emitVel.subarray(0, actualCount * 2));

    gl.bindBuffer(gl.ARRAY_BUFFER, activeSet[2]);
    gl.bufferSubData(gl.ARRAY_BUFFER, this.writeHead * 3 * 4, this.emitColor.subarray(0, actualCount * 3));

    gl.bindBuffer(gl.ARRAY_BUFFER, activeSet[3]);
    gl.bufferSubData(gl.ARRAY_BUFFER, this.writeHead * 1 * 4, this.emitLife.subarray(0, actualCount));

    gl.bindBuffer(gl.ARRAY_BUFFER, activeSet[4]);
    gl.bufferSubData(gl.ARRAY_BUFFER, this.writeHead * 1 * 4, this.emitMaxLife.subarray(0, actualCount));

    gl.bindBuffer(gl.ARRAY_BUFFER, null);

    // Shift circular buffer write pointer
    this.writeHead = (this.writeHead + actualCount) % this.maxParticles;
  }

  updateAndRender(config: SimulationConfig, camera: { x: number, y: number, scale: number }, deltaTimeMs: number) {
    const gl = this.context.gl;

    // Setup rendering constants
    const gravity = config.collisionEffectGravity !== undefined ? config.collisionEffectGravity * 0.001 : 0.015;
    const damping = 0.985; // Air drag coefficient

    const sourceIdx = this.activeIndex;
    const targetIdx = 1 - this.activeIndex;

    // --- STEP 1: SOLVE PARTICLE LAWS ON GPU TRANSFORM FEEDBACK PASS ---
    this.context.useProgram(this.updateProgram);
    gl.bindVertexArray(this.updateVaos[sourceIdx]);

    // Pass timestep metrics
    gl.uniform1f(gl.getUniformLocation(this.updateProgram, 'u_deltaTime'), deltaTimeMs);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram, 'u_gravity'), gravity);
    gl.uniform1f(gl.getUniformLocation(this.updateProgram, 'u_damping'), damping);

    // Disable draw rasterization to direct outputs safely to feed buffers
    gl.enable(gl.RASTERIZER_DISCARD);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, this.transformFeedbacks[targetIdx]);
    
    gl.beginTransformFeedback(gl.POINTS);
    gl.drawArrays(gl.POINTS, 0, this.maxParticles);
    gl.endTransformFeedback();

    // Restore standard pipeline
    gl.disable(gl.RASTERIZER_DISCARD);
    gl.bindTransformFeedback(gl.TRANSFORM_FEEDBACK, null);

    // --- STEP 2: RASTERIZE INTEGRATED PIPELINE ---
    this.context.useProgram(this.renderProgram);
    gl.bindVertexArray(this.renderVaos[targetIdx]); // Render from Target buffers containing newborn states

    WebGLUtils.updateCommonUniforms(gl, this.renderProgram, this.context.width, this.context.height, camera, 1.0);
    gl.uniform1f(gl.getUniformLocation(this.renderProgram, 'u_baseSize'), (config.collisionEffectScale !== undefined ? config.collisionEffectScale * 4.0 : 4.0));

    // Enable additive blend to give gorgeous glowing particles feel
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // Additive synthesis

    gl.drawArrays(gl.POINTS, 0, this.maxParticles);

    // Restore standard blend state configured in WebGLContext
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    gl.bindVertexArray(null);

    // Ping-pong index toggle
    this.activeIndex = targetIdx;
  }

  reset() {
    // Clear all registers
    const gl = this.context.gl;
    this.writeHead = 0;
    
    for (let setIdx = 0; setIdx < 2; setIdx++) {
      const activeBufs = this.buffers[setIdx];
      for (let i = 0; i < activeBufs.length; i++) {
        gl.bindBuffer(gl.ARRAY_BUFFER, activeBufs[i]);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.glZeros.subarray(0, this.maxParticles * (i === 2 ? 3 : (i === 0 || i === 1 ? 2 : 1))));
      }
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }
}
